import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import DocAccordion from './components/DocAccordion';
import { patriotDocumentsData } from '@/mocks/documents';
import type { DocItem } from '@/mocks/documents';
import { Link } from 'react-router-dom';

function getStoredDocs(): DocItem[] {
  try {
    const raw = localStorage.getItem('admin_patriot_docs');
    return raw !== null ? JSON.parse(raw) : patriotDocumentsData;
  } catch {
    return patriotDocumentsData;
  }
}

export default function PatriotDocumentsPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<DocItem[]>(() => getStoredDocs());
  const lang = i18n.language || 'kk';

  const isEn = lang.startsWith('en');
  const isRu = lang.startsWith('ru');

  useEffect(() => {
    // 1. Fetch from server API if hosted
    fetch('/api/docs.php?type=patriot')
      .then((res) => (res.ok ? res.json() : null))
      .then((serverDocs) => {
        if (Array.isArray(serverDocs)) {
          setItems(serverDocs);
          localStorage.setItem('admin_patriot_docs', JSON.stringify(serverDocs));
        }
      })
      .catch(() => {});

    // 2. Listen to storage changes
    const handleStorage = () => setItems(getStoredDocs());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />

      {/* Hero Banner */}
      <div className="relative h-56 md:h-72 flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=military%20cadets%20youth%20patriots%20in%20formation%20training%20discipline%20national%20flag%20background%20tactical%20lighting%20cinematic&width=1600&height=500&seq=patriot-banner-01&orientation=landscape"
            alt="Patriot Documentation"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/70 to-background-950/30" />
        </div>
        <div className="relative w-full px-4 md:px-12 pb-10">
          <div className="flex items-center gap-2 text-xs text-foreground-600 mb-3 font-label tracking-widest uppercase">
            <Link to="/" className="hover:text-accent-500 transition-colors">
              {t('nav.home', isEn ? 'Home' : isRu ? 'Главная' : 'Басты бет')}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-400">
              {t('nav.docs_patriot', isEn ? '«Patriot» Documentation' : isRu ? 'Документация «Patriot»' : '«Patriot» құжаттамасы')}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl text-foreground-50 uppercase tracking-widest">
            {isEn ? '«Patriot» Documents' : isRu ? 'Документы «Patriot»' : '«Patriot» құжаттары'}
          </h1>
        </div>
      </div>

      {/* Content */}
      <main className="w-full max-w-5xl mx-auto px-4 md:px-8 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-8 h-px bg-accent-500" />
          <p className="text-xs font-label tracking-widest uppercase text-accent-500">
            {isEn ? 'VPC «Patriot» Official Regulations' : isRu ? 'Официальные положения ВПК «Патриот»' : '«Патриот» ӘПК ресми ережелері'}
          </p>
        </div>

        <DocAccordion items={items} />
      </main>

      <Footer />
    </div>
  );
}